import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Shield,
  AlertTriangle,
  Scale
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import complianceRuleMasterService from '../../services/complianceRuleMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ComplianceRuleMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalRules: 0,
    activeRules: 0,
    inactiveRules: 0,
    regulatoryCategory: 0,
    legalCategory: 0,
    financialCategory: 0,
    criticalPriority: 0,
    highPriority: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [rulesResponse, statsResponse] = await Promise.all([
        complianceRuleMasterService.getAllComplianceRules(),
        complianceRuleMasterService.getComplianceRuleStats()
      ])
      
      if (rulesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (rulesResponse.data || []).map(rule => ({
          ...rule,
          id: rule._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (rulesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No compliance rule records found. Create your first compliance rule!')
        }
      } else {
        toast.error(rulesResponse.message || 'Failed to load compliance rules')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading compliance rule master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view compliance rules')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load compliance rule master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getCategoryBadgeClass = (category) => {
    const categoryMap = {
      'Regulatory': 'danger',
      'Legal': 'warning',
      'Financial': 'pending',
      'Data Protection': 'active',
      'Industry Specific': 'pending',
      'Internal Policy': 'inactive',
      'Other': 'inactive'
    }
    return categoryMap[category] || 'inactive'
  }

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      'Critical': 'danger',
      'High': 'warning',
      'Medium': 'pending',
      'Low': 'inactive'
    }
    return priorityMap[priority] || 'inactive'
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Active': 'active',
      'Inactive': 'inactive',
      'Superseded': 'warning',
      'Archived': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'complianceRuleCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'complianceRuleName',
      header: 'Rule Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getCategoryBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getPriorityBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'regulatoryAuthority',
      header: 'Authority',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/contracts/compliance-rule-master/form')
  }

  const handleEdit = (ruleData) => {
    navigate('/contracts/compliance-rule-master/edit', { state: { ruleData } })
  }

  const handleView = (ruleData) => {
    navigate('/contracts/compliance-rule-master/view', { state: { ruleData } })
  }

  const handleDelete = (ruleData) => {
    setDeleteItem(ruleData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting compliance rule...')
      const response = await complianceRuleMasterService.deleteComplianceRule(deleteItem._id)
      if (response.success) {
        toast.success('Compliance rule deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete compliance rule')
      }
    } catch (error) {
      console.error('Error deleting compliance rule:', error)
      if (error.response?.status === 404) {
        toast.error('Compliance rule not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this compliance rule')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete compliance rule. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Contracts', path: '/contracts' },
          { label: 'Compliance Rules', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Compliance Rules</h1>
          <p>Regulatory compliance and audit requirements</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Rule
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Shield size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalRules || 0}</div>
          <div className="metric-title">Total Rules</div>
          <div className="metric-subtitle">All compliance rules</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Shield size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeRules || 0}</div>
          <div className="metric-title">Active Rules</div>
          <div className="metric-subtitle">Currently enforced</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Critical</span>
            </div>
          </div>
          <div className="metric-value">{stats.criticalPriority || 0}</div>
          <div className="metric-title">Critical Priority</div>
          <div className="metric-subtitle">Highest severity</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>High</span>
            </div>
          </div>
          <div className="metric-value">{stats.highPriority || 0}</div>
          <div className="metric-title">High Priority</div>
          <div className="metric-subtitle">Elevated attention</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <Scale size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Regulatory</span>
            </div>
          </div>
          <div className="metric-value">{stats.regulatoryCategory || 0}</div>
          <div className="metric-title">Regulatory</div>
          <div className="metric-subtitle">Regulatory category</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Scale size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Legal</span>
            </div>
          </div>
          <div className="metric-value">{stats.legalCategory || 0}</div>
          <div className="metric-title">Legal</div>
          <div className="metric-subtitle">Legal category</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Compliance Rule"
        message={`Are you sure you want to delete compliance rule "${deleteItem?.complianceRuleName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ComplianceRuleMaster;